import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Database,
  Table,
  HardDrive,
  CheckCircle2,
  Zap,
  AlertCircle,
  FileSpreadsheet,
  Layers,
  ArrowRight
} from 'lucide-react';

export interface DatabaseNodeData {
  collectionName?: string;
  tableCategory?: 'crm_leads' | 'tax_filings' | 'booking_reservations' | 'survey_feedback' | 'custom_records';
  autoIndexFields?: boolean;
  enableCsvSync?: boolean;
  retentionDays?: number;
  isCustomizable?: boolean;
  [key: string]: unknown;
}

export const DatabaseNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as DatabaseNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof DatabaseNodeData, value: any, allUpdates?: Partial<DatabaseNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const res = await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'DATABASE_STORAGE',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[DatabaseNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof DatabaseNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const tableCategory = nodeData.tableCategory || 'crm_leads';
  const autoIndexFields = nodeData.autoIndexFields !== false;
  const enableCsvSync = nodeData.enableCsvSync !== false;

  return (
    <div
      className={`group rounded-2xl border transition-all duration-300 w-[380px] select-none relative backdrop-blur-xl bg-gradient-to-b from-[#051817]/98 via-[#030F0E]/98 to-[#010606]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-teal-400 ring-2 ring-teal-400/50 shadow-[0_0_35px_rgba(20,184,166,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-teal-500/40 hover:border-teal-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(20,184,166,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Live Data in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('formLeadNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-teal-950 via-slate-900 to-emerald-950 border-2 border-teal-400 text-teal-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(20,184,166,0.8)] hover:border-teal-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data to view CRM leads and database storage"
          >
            <Table size={13} className="text-teal-300 animate-pulse" />
            <span>View Data in Portal</span>
            <ArrowRight size={13} className="text-teal-400" />
          </button>
        </div>
      )}
      {/* ── Directional Connection Handles (Left in from Form / Right out) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-db"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Input from Luna Core Hub (out-actions)"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-db"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Output: Chained notification / webhook after persistence"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(168,85,247,0.8)]"
      />

      {/* ── Node Header ──────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-teal-950/80 via-cyan-950/60 to-slate-950/80 px-4 py-3 border-b border-teal-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-teal-500/20 border border-teal-400/40 text-teal-300 shadow-[0_0_12px_rgba(20,184,166,0.3)]">
            <Database size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Database & CRM Storage</h4>
            <p className="text-[10px] text-teal-300/70 font-mono">Structured Record Persistence</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-300 border border-teal-500/30">
            STORAGE
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {/* ── Node Body & Parameter Inputs ──────────────────────────────── */}
      <div className="p-4 space-y-3.5 text-xs">
        {/* Table / Collection Category */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span>Target Table / Collection</span>
            <span className="text-teal-400 text-[9px]">SQL / CRM</span>
          </label>
          <select
            value={tableCategory}
            onChange={(e) => handleChange('tableCategory', e.target.value)}
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-2 text-slate-100 text-xs font-medium focus:outline-none focus:border-teal-400 transition cursor-pointer"
          >
            <option value="crm_leads">Client Leads & CRM Inquiries (TenantFormSubmission)</option>
            <option value="tax_filings">Tax Returns & Document Filings</option>
            <option value="booking_reservations">Reservations & Hotel/Room Bookings</option>
            <option value="survey_feedback">Customer Surveys & Feedback Records</option>
            <option value="custom_records">Custom Schema Collection (TenantWidgetData)</option>
          </select>
        </div>

        {/* Custom Collection Identifier */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
            Collection Name / Tag
          </label>
          <input
            type="text"
            value={nodeData.collectionName || ''}
            onChange={(e) => handleChange('collectionName', e.target.value)}
            placeholder="e.g. client_onboarding_2026 or tax_clients"
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-2 text-slate-100 text-xs font-mono font-bold focus:outline-none focus:border-teal-400 transition"
          />
        </div>

        {/* Storage Flags */}
        <div className="space-y-2 pt-1 border-t border-white/5">
          <label className="flex items-center justify-between cursor-pointer p-2 rounded-xl bg-slate-900/60 border border-white/5 hover:border-teal-500/30 transition">
            <span className="text-[11px] text-slate-300 font-medium flex items-center gap-2">
              <Layers size={13} className="text-teal-400" /> Auto-Index Form Answer Keys
            </span>
            <input
              type="checkbox"
              checked={autoIndexFields}
              onChange={(e) => handleChange('autoIndexFields', e.target.checked)}
              className="nodrag w-4 h-4 rounded border-white/20 text-teal-600 focus:ring-teal-500 cursor-pointer accent-teal-500"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer p-2 rounded-xl bg-slate-900/60 border border-white/5 hover:border-teal-500/30 transition">
            <span className="text-[11px] text-slate-300 font-medium flex items-center gap-2">
              <FileSpreadsheet size={13} className="text-emerald-400" /> Enable CSV/Excel Export in Portal Data
            </span>
            <input
              type="checkbox"
              checked={enableCsvSync}
              onChange={(e) => handleChange('enableCsvSync', e.target.checked)}
              className="nodrag w-4 h-4 rounded border-white/20 text-emerald-600 focus:ring-emerald-500 cursor-pointer accent-emerald-500"
            />
          </label>
        </div>

        {/* Portal Data Tab Direct Link */}
        {openPortalData && (
          <button
            type="button"
            onClick={() => openPortalData('formLeadNode', id)}
            className="w-full mt-2 py-2 px-3 rounded-xl bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/30 text-teal-300 text-xs font-bold flex items-center justify-center gap-2 transition"
          >
            <Table size={13} /> View Saved Records in Portal Data <ArrowRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
});

DatabaseNode.displayName = 'DatabaseNode';
export default DatabaseNode;
